import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import {
  Upload,
  FileText,
  X,
  CheckCircle2,
  AlertCircle,
  Send,
  Users,
  TrendingUp,
  Award,
  MapPin,
  Clock,
  Briefcase,
} from 'lucide-react'

interface FileUpload {
  file: File
  progress: number
  status: 'uploading' | 'success' | 'error'
}

export function CareersSection() {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    message: '',
    privacy: false,
  })
  const [uploadedFile, setUploadedFile] = useState<FileUpload | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const benefits = [
    {
      icon: Users,
      title: t('careers.benefits.0.title'),
      desc: t('careers.benefits.0.desc'),
      stat: '125+',
      statLabel: t('careers.stats.0.label'),
    },
    {
      icon: TrendingUp,
      title: t('careers.benefits.1.title'),
      desc: t('careers.benefits.1.desc'),
      stat: '36',
      statLabel: t('careers.stats.1.label'),
    },
    {
      icon: Award,
      title: t('careers.benefits.2.title'),
      desc: t('careers.benefits.2.desc'),
      stat: '5',
      statLabel: t('careers.stats.2.label'),
    },
  ]

  const positions = [
    { value: '', label: t('careers.positionPlaceholder') },
    { value: 'software', label: t('careers.positions.software') },
    { value: 'electrical', label: t('careers.positions.electrical') },
    { value: 'mechanical', label: t('careers.positions.mechanical') },
    { value: 'commissioning', label: t('careers.positions.commissioning') },
    { value: 'sales', label: t('careers.positions.sales') },
    { value: 'other', label: t('careers.otherPosition') },
  ]

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      handleFile(files[0])
    }
  }, [])

  const handleFile = (file: File) => {
    if (file.type !== 'application/pdf' && !file.name.endsWith('.pdf')) {
      setUploadedFile({ file, progress: 0, status: 'error' })
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadedFile({ file, progress: 0, status: 'error' })
      return
    }

    setUploadedFile({ file, progress: 0, status: 'uploading' })

    let progress = 0
    const interval = setInterval(() => {
      progress += 10
      if (progress >= 100) {
        clearInterval(interval)
        setUploadedFile({ file, progress: 100, status: 'success' })
      } else {
        setUploadedFile({ file, progress, status: 'uploading' })
      }
    }, 100)
  }

  const removeFile = () => {
    setUploadedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.privacy) return

    setIsSubmitting(true)
    await new Promise(resolve => setTimeout(resolve, 1500))
    setIsSubmitting(false)
    setSubmitStatus('success')

    setTimeout(() => {
      setFormData({ name: '', email: '', phone: '', position: '', message: '', privacy: false })
      setUploadedFile(null)
      setSubmitStatus('idle')
    }, 3000)
  }

  return (
    <section ref={ref} className="bg-slate-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('careers.formTitle')}
          </h2>
          <div className="w-20 h-1 bg-[#f75e47] mx-auto mb-6" />
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('careers.subtitle')}
          </p>
        </motion.div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Left: Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-4"
          >
            <div className="sticky top-24 space-y-6">
              {/* Benefits Cards */}
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                  className="group rounded-2xl bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#f75e47]/10 flex items-center justify-center group-hover:bg-[#f75e47] transition-colors duration-300">
                      <benefit.icon className="w-6 h-6 text-[#f75e47] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-slate-900 group-hover:text-[#f75e47] transition-colors">
                        {benefit.title}
                      </h4>
                      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>
                  {/* Decorative line on hover */}
                  <div className="mt-4 h-0.5 w-0 bg-[#f75e47] transition-all duration-300 group-hover:w-12" />
                </motion.div>
              ))}

              {/* Stats Grid */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="grid grid-cols-3 gap-3"
              >
                {benefits.map((benefit, index) => (
                  <div
                    key={`stat-${index}`}
                    className="text-center p-4 rounded-xl bg-white shadow-sm"
                  >
                    <div className="text-xl font-bold text-[#f75e47]">
                      {benefit.stat}
                    </div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                      {benefit.statLabel}
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* Location Info Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="rounded-2xl bg-white p-6 shadow-sm"
              >
                <div className="flex items-center gap-2 text-slate-900 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-[#f75e47]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#f75e47]" />
                  </div>
                  <span className="font-semibold">{t('careers.locations.title')}</span>
                </div>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#f75e47]" />
                    <span>{t('careers.locations.pravisdomini')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                    <span>{t('careers.locations.sanVito')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                    <span>{t('careers.locations.barletta')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-slate-400" />
                    <span>{t('careers.locations.catania')}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right: Application Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-8"
          >
            <div className="rounded-2xl bg-white p-6 md:p-8 shadow-sm">
              {/* Form Header */}
              <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#f75e47]/10 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-[#f75e47]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-slate-900">
                    {t('careers.formTitle')}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {t('careers.form.instructions')}
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-1.5 text-xs text-slate-400">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{t('careers.form.estimatedTime')}</span>
                </div>
              </div>

              {/* Form Content */}
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-900 mb-2">
                    {t('careers.form.successTitle')}
                  </h3>
                  <p className="text-slate-600 max-w-md mx-auto">
                    {t('careers.success')}
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Info Grid */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {t('careers.name')} <span className="text-[#f75e47]">*</span>
                      </label>
                      <input
                        required
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t('careers.placeholders.name')}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#f75e47] focus:ring-2 focus:ring-[#f75e47]/20 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {t('careers.email')} <span className="text-[#f75e47]">*</span>
                      </label>
                      <input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder={t('careers.placeholders.email')}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#f75e47] focus:ring-2 focus:ring-[#f75e47]/20 outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {t('careers.phone')}
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder={t('careers.placeholders.phone')}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#f75e47] focus:ring-2 focus:ring-[#f75e47]/20 outline-none transition-all"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">
                        {t('careers.position')} <span className="text-[#f75e47]">*</span>
                      </label>
                      <select
                        required
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 focus:border-[#f75e47] focus:ring-2 focus:ring-[#f75e47]/20 outline-none transition-all appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 12px center',
                          backgroundSize: '16px'
                        }}
                      >
                        {positions.map((pos) => (
                          <option key={pos.value} value={pos.value}>
                            {pos.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      {t('careers.coverLetter')}
                    </label>
                    <textarea
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder={t('careers.placeholders.coverLetter')}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-[#f75e47] focus:ring-2 focus:ring-[#f75e47]/20 outline-none transition-all resize-none"
                    />
                  </div>

                  {/* CV Upload */}
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-slate-700">
                      {t('careers.cv')} <span className="text-[#f75e47]">*</span>
                    </label>

                    {!uploadedFile ? (
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`
                          relative border-2 border-dashed rounded-xl p-8 cursor-pointer
                          transition-all duration-300 group
                          ${isDragging
                            ? 'border-[#f75e47] bg-[#f75e47]/5'
                            : 'border-slate-300 bg-slate-50 hover:border-[#f75e47]/50 hover:bg-slate-100'
                          }
                        `}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                          className="hidden"
                        />
                        <div className="text-center">
                          <div className={`
                            w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center
                            transition-all duration-300
                            ${isDragging ? 'bg-[#f75e47]/10' : 'bg-white group-hover:bg-[#f75e47]/10'}
                          `}>
                            <Upload className={`w-8 h-8 transition-colors ${isDragging ? 'text-[#f75e47]' : 'text-slate-400 group-hover:text-[#f75e47]'}`} />
                          </div>
                          <p className="text-slate-900 font-medium mb-1">
                            {isDragging ? t('careers.upload.dragTextActive') : t('careers.upload.dragText')}
                          </p>
                          <p className="text-sm text-slate-500 mb-3">
                            {t('careers.upload.clickText')}
                          </p>
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white text-xs text-slate-500 border border-slate-200">
                            <FileText className="w-3.5 h-3.5" />
                            <span>{t('careers.upload.fileSpec')}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`
                          border rounded-xl p-4
                          ${uploadedFile.status === 'error'
                            ? 'border-red-300 bg-red-50'
                            : 'border-slate-200 bg-slate-50'
                          }
                        `}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`
                            w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                            ${uploadedFile.status === 'success' ? 'bg-green-100' : ''}
                            ${uploadedFile.status === 'uploading' ? 'bg-[#f75e47]/10' : ''}
                            ${uploadedFile.status === 'error' ? 'bg-red-100' : ''}
                          `}>
                            {uploadedFile.status === 'success' && <CheckCircle2 className="w-6 h-6 text-green-600" />}
                            {uploadedFile.status === 'uploading' && (
                              <div className="w-5 h-5 border-2 border-[#f75e47] border-t-transparent rounded-full animate-spin" />
                            )}
                            {uploadedFile.status === 'error' && <AlertCircle className="w-6 h-6 text-red-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-900 font-medium truncate">
                              {uploadedFile.file.name}
                            </p>
                            <p className="text-sm text-slate-500">
                              {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                            {uploadedFile.status === 'uploading' && (
                              <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${uploadedFile.progress}%` }}
                                  className="h-full bg-[#f75e47] rounded-full"
                                />
                              </div>
                            )}
                            {uploadedFile.status === 'error' && (
                              <p className="text-xs text-red-500 mt-1">
                                {t('careers.upload.errorMessage')}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-2 rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Privacy Checkbox */}
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                    <input
                      id="privacy"
                      type="checkbox"
                      required
                      checked={formData.privacy}
                      onChange={(e) => setFormData({ ...formData, privacy: e.target.checked })}
                      className="mt-0.5 w-5 h-5 rounded border-slate-300 text-[#f75e47] focus:ring-[#f75e47] focus:ring-offset-0 cursor-pointer"
                    />
                    <label htmlFor="privacy" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                      {t('careers.privacy')} <span className="text-[#f75e47]">*</span>
                    </label>
                  </div>

                  {/* Error Message */}
                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 text-sm p-3 rounded-lg bg-red-50 border border-red-200">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{t('careers.error')}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.privacy || !uploadedFile || uploadedFile.status !== 'success'}
                    className="
                      w-full group relative
                      bg-[#f75e47] hover:bg-[#e04d36]
                      disabled:bg-slate-300 disabled:cursor-not-allowed
                      text-white font-semibold
                      rounded-xl px-6 py-4
                      transition-all duration-300
                      flex items-center justify-center gap-3
                    "
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>{t('careers.upload.submitting')}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{t('careers.send')}</span>
                      </>
                    )}
                  </button>

                  {/* Form Footer */}
                  <div className="flex items-center justify-center gap-6 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      <span>{t('careers.security.ssl')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <div className="w-2 h-2 rounded-full bg-[#f75e47]" />
                      <span>{t('careers.security.gdpr')}</span>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
